"use client";

import {
  UserPlus,
  MapPin,
  Globe,
  Check,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRouter } from "next/navigation";
import { SocialIcon } from "@/components/svg/social";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Logo } from "@/customcomponents/ui/customs/logo";

const platformIconMapping = {
  youtube: "youtube",
  instagram: "instagram",
  twitter: "x",
  facebook: "facebook",
  tiktok: "tiktok",
  twitch: "twitch",
};

// Text colors per platform (icons only), keep neutral background
const platformTextColors = {
  youtube: "text-red-500",
  instagram: "text-pink-500",
  twitter: "text-blue-400",
  facebook: "text-blue-600",
  tiktok: "text-gray-800",
  twitch: "text-purple-500",
};

// Category colors for better visual hierarchy
const categoryColors = [
  "bg-gradient-to-r from-blue-50 to-indigo-100 text-blue-700 border-blue-200",
  "bg-gradient-to-r from-purple-50 to-violet-100 text-purple-700 border-purple-200", 
  "bg-gradient-to-r from-emerald-50 to-green-100 text-emerald-700 border-emerald-200",
  "bg-gradient-to-r from-orange-50 to-amber-100 text-orange-700 border-orange-200",
  "bg-gradient-to-r from-pink-50 to-rose-100 text-pink-700 border-pink-200",
  "bg-gradient-to-r from-cyan-50 to-sky-100 text-cyan-700 border-cyan-200",
];

// Updated interface to match Meilisearch hit data
interface Creator {
  _id: string;
  name: string;
  full_name?: string;
  description_short?: string;
  description_long?: string;
  category?: string[] | string;
  country?: string[] | string;
  language?: string[] | string;
  label?: string[] | string;
  avatar?: {
    image: string;
    fallback?: string;
  };
  socialStats?: {
    youtube?: number;
    instagram?: number;
    twitter?: number;
    facebook?: number;
    tiktok?: number;
    twitch?: number;
    lastUpdated?: number;
  };
  hasSocialMedia?: {
    youtube?: boolean;
    instagram?: boolean;
    twitter?: boolean;
    facebook?: boolean;
    tiktok?: boolean;
    twitch?: boolean;
  };
  engagementRates?: {
    youtube?: number;
    instagram?: number;
    twitter?: number;
    facebook?: number;
    tiktok?: number;
    twitch?: number;
  };
  performanceMetrics?: {
    completionRate?: number;
    conversionRate?: number;
    engagementRate?: number;
    responseTime?: number;
    lastUpdated?: number;
  };
  verified?: boolean;
  avg_price?: string | number;
  currency?: string;
}

interface CreatorCardProps {
  creator: Creator;
  onViewProfile?: (creatorId: string) => void;
  onInviteToCampaign?: (creatorId: string) => void;
  canInvite?: boolean;
  campaignId?: string;
  inviteBasePath?: string;
  campaign?: {
    socialPlatforms?: string[];
    name?: string;
  };
}

// Helper functions
function formatFollowerCount(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
}

function normalizeArray(value?: string[] | string): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
}

function getSocialMediaUrl(platform: string, username: string): string {
  const baseUrls = {
    youtube: "https://youtube.com/@",
    instagram: "https://instagram.com/",
    twitter: "https://twitter.com/",
    facebook: "https://facebook.com/",
    tiktok: "https://tiktok.com/@",
    twitch: "https://twitch.tv/",
  };
  return (
    baseUrls[platform as keyof typeof baseUrls] + username.replace("@", "")
  );
}

function calculateAverageEngagement(
  engagementRates?: Creator["engagementRates"]
): number {
  if (!engagementRates) return 0;
  const rates = Object.values(engagementRates).filter(
    (rate): rate is number => typeof rate === "number" && !Number.isNaN(rate)
  );
  return rates.length > 0 ? rates.reduce((a, b) => a + b, 0) / rates.length : 0;
}

function hasRequiredPlatforms(
  creator: Creator,
  campaignPlatforms?: string[]
): boolean {
  if (!campaignPlatforms || campaignPlatforms.length === 0) return true;
  
  return campaignPlatforms.some((platform) => {
    const hasAccount = creator.hasSocialMedia?.[platform as keyof typeof creator.hasSocialMedia];
    const hasFollowers = creator.socialStats?.[platform as keyof typeof creator.socialStats] && 
                        creator.socialStats[platform as keyof typeof creator.socialStats]! > 0;
    return hasAccount || hasFollowers;
  });
}

function getUnavailablePlatforms(
  creator: Creator,
  campaignPlatforms?: string[]
): string[] {
  if (!campaignPlatforms || campaignPlatforms.length === 0) return [];
  
  return campaignPlatforms.filter((platform) => {
    const hasAccount = creator.hasSocialMedia?.[platform as keyof typeof creator.hasSocialMedia];
    const hasFollowers = creator.socialStats?.[platform as keyof typeof creator.socialStats] && 
                        creator.socialStats[platform as keyof typeof creator.socialStats]! > 0;
    return !hasAccount && !hasFollowers;
  });
}


export function CreatorCard({
  creator,
  onViewProfile,
  onInviteToCampaign,
  canInvite,
  campaignId,
  inviteBasePath,
  campaign,
}: CreatorCardProps) {
  const router = useRouter();

  // Normalize arrays
  const categories = normalizeArray(creator.category);
  const countries = normalizeArray(creator.country);
  const languages = normalizeArray(creator.language);
  const labels = normalizeArray(creator.label);

  // Calculate pricing fallback
  const estimatedPrice = creator.avg_price
    ? typeof creator.avg_price === "string"
      ? Number.parseInt(creator.avg_price.replace(/[^0-9]/g, ""), 10) || 100
      : typeof creator.avg_price === "number"
        ? creator.avg_price
        : 100
    : 100;

  // Process social media platforms
  const socialPlatforms = Object.entries(creator.hasSocialMedia || {})
    .filter(([, isConnected]) => isConnected)
    .map(([platform]) => {
      const followerCount =
        creator.socialStats?.[platform as keyof typeof creator.socialStats] ||
        0;
      const hasData = followerCount > 0;

      return {
        name: platform,
        followers: followerCount,
        hasData,
        url: getSocialMediaUrl(platform, creator.name),
      };
    });

  // Calculate average engagement
  const avgEngagement = calculateAverageEngagement(creator.engagementRates);

  // Check platform availability for campaign
  const canInviteCreator = hasRequiredPlatforms(creator, campaign?.socialPlatforms);
  const unavailablePlatforms = getUnavailablePlatforms(creator, campaign?.socialPlatforms);

  const handleViewProfile = () => {
    if (onViewProfile) {
      onViewProfile(creator._id);
    }
  };

  const handleInviteToCampaign = () => {
    if (canInvite && campaignId && canInviteCreator) {
      const basePath = inviteBasePath || `/v1/search/advanced/connect`;
      router.push(`${basePath}/${campaignId}/${creator._id}`);
    } else if (onInviteToCampaign && canInviteCreator) {
      onInviteToCampaign(creator._id);
    }
  };


    return (
      <Card
      className="group relative border border-slate-200 shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 ease-out h-full cursor-pointer bg-white rounded-2xl hover:border-slate-300"
        data-creator-card
        onClick={handleViewProfile}
      >
        {/* Status Indicator - Beautiful gradient at top */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
        
      <CardContent className="p-6 flex-1 flex flex-col">
        {/* Top Section - Avatar, Name, Engagement, Category */}
        <div className="flex gap-4 mb-4">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <div className="relative w-20 h-20">
              <Logo
                src={creator.avatar?.image}
                alt={creator.name}
                containerClassName="w-full h-full rounded-2xl overflow-hidden bg-slate-100 flex items-center justify-center"
                imageClassName="w-full h-full object-cover rounded-2xl"
                fallbackClassName="h-10 w-10 text-slate-400"
                fallbackIcon={User}
                showFallbackWhileLoading={false}
              />
              {creator.verified && (
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                </div>
              )}
            </div>
          </div>

          {/* Name, Engagement, Category - Next to Avatar */}
          <div className="flex-1 flex flex-col justify-center min-w-0">
            {/* Name */}
            <h3 className="font-bold text-lg text-slate-900 mb-1 line-clamp-1">
              {creator.name}
            </h3>

            {/* Engagement */}
            <div className="text-sm text-slate-600">
              {avgEngagement.toFixed(2)}% Engagement
            </div>

            {/* Category below engagement */}
            {categories.length > 0 && (
              <div className="text-sm text-slate-600 mt-0.5">
                {categories[0]}
                {categories.length > 2 && (
                  <span> and {categories.length - 1} more</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content Section - All below avatar */}
        <div className="flex-1 flex flex-col">
          {/* Description */}
          <div className="mb-3 h-16">
            <p className="text-slate-600 text-sm leading-relaxed line-clamp-3 h-full overflow-hidden">
              {creator.description_short || creator.description_long || "A versatile gamer and content creator focusing on games and collaborations."}
            </p>
          </div>

          {/* Location and Language in one row */}
          <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
            {countries.length > 0 && (
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span>{countries[0]}</span>
              </div>
            )}
            {languages.length > 0 && (
              <div className="flex items-center gap-1">
                <Globe className="h-3 w-3" />
                <span>{languages[0]}</span>
              </div>
            )}
          </div>

            {/* Social Media Stats - More compact */}
            <div className="flex items-center gap-4 mb-3">
              {socialPlatforms.slice(0, 4).map((platform) => {
                const iconKey = platformIconMapping[platform.name as keyof typeof platformIconMapping];
                return (
                  <div key={platform.name} className="flex items-center gap-1.5 text-sm">
                    {iconKey ? (
                      <SocialIcon 
                        platform={iconKey}
                        className={`w-4 h-4 ${platformTextColors[platform.name as keyof typeof platformTextColors] || "text-slate-700"}`}
                        isSelected={true}
                      />
                    ) : (
                      <span className="text-sm">📱</span>
                    )}
                    {platform.hasData ? (
                      <span className="font-semibold text-slate-700">
                        {formatFollowerCount(platform.followers)}
                      </span>
                    ) : (
                      <Check className="w-3 h-3 text-green-500" />
                    )}
                  </div>
                );
              })}
            </div>

          {/* Tags - More compact, always single line */}
          <div className="mb-3 min-h-8 flex items-start">
            {labels.length > 0 ? (
              <div className="flex gap-1 flex-nowrap items-center overflow-hidden w-full">
                {labels.slice(0, 2).map((tag: string) => (
                  <Badge key={tag} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 px-2 py-0.5 rounded-md whitespace-nowrap flex-shrink-0 max-w-[120px] truncate">
                    {tag}
                  </Badge>
                ))}
                {labels.length > 2 && (
                  <Badge 
                    variant="outline" 
                    className="text-xs bg-slate-50 text-slate-500 border-slate-200 px-2 py-0.5 rounded-md whitespace-nowrap flex-shrink-0"
                  >
                    and {labels.length - 2} more
                  </Badge>
                )}
              </div>
            ) : null}
          </div>

          {/* Divider */}
          <div className="border-t border-slate-200 my-4"></div>

          {/* Bottom Section - Price and Button */}
          <div className="mt-auto flex items-center justify-between">
            {/* Price */}
            <div className="text-center flex-1">
              <div className="text-sm text-slate-500 mb-2">Starting from</div>
              <div className="text-3xl font-bold text-slate-900">
                ${estimatedPrice >= 1000 ? `${(estimatedPrice / 1000).toFixed(2).replace(/\.?0+$/, '')}K` : estimatedPrice.toLocaleString()}
              </div>
            </div>

            {/* Invite Button */}
            {(canInvite && campaignId) && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        size="sm"
                        className={`font-medium py-2 px-3 rounded-lg transition-colors text-sm ml-4 ${
                          canInviteCreator
                            ? "bg-blue-600 hover:bg-blue-700 text-white"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                        disabled={!canInviteCreator}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canInviteCreator) {
                            handleInviteToCampaign();
                          }
                        }}
                      >
                        <UserPlus className="h-3 w-3 mr-1" />
                        Invite
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!canInviteCreator && (
                    <TooltipContent 
                      color="destructive" 
                      side="left" 
                      className="z-[9999] max-w-xs whitespace-normal break-words"
                      sideOffset={8}
                    >
                      <p className="text-sm">
                        Creator does not have accounts on the required platforms: {unavailablePlatforms.join(", ")}
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        </CardContent>
      </Card>
  );
}

export default CreatorCard;